function asyncGeneratorStep(n, t, e, r, o, a, c) { try { var i = n[a](c), u = i.value; } catch (n) { return void e(n); } i.done ? t(u) : Promise.resolve(u).then(r, o); }
function _asyncToGenerator(n) { return function () { var t = this, e = arguments; return new Promise(function (r, o) { var a = n.apply(t, e); function _next(n) { asyncGeneratorStep(a, r, o, _next, _throw, "next", n); } function _throw(n) { asyncGeneratorStep(a, r, o, _next, _throw, "throw", n); } _next(void 0); }); }; }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
// ----------------------------------------------------------------------------
// Product Form Web Component: iamVariantPicker <iam-variant-picker>
// Extends: iamFormElement
// ----------------------------------------------------------------------------
// The Variant Picker is responsible for handling the variant selection UI.
// - Highlights the selected variant options
// - Enables/disables options based on variant availability
// ----------------------------------------------------------------------------
// Event Listeners:
// - `productForm:option:changed`       → Listens for variant option changes to
//                                        update the variant selector UI
// - `mouseenter` and `mouseleave`      → Listens for hover events to toggle swatch
//                                        option labels
// ----------------------------------------------------------------------------
// Event Filters:
// - `productForm:variant:availability` → Allows other modules to filter the
//                                        variant availability based on custom logic
// ----------------------------------------------------------------------------
// Gotchas:
// - Swatch labels must be properly structured within `.swatch__option` to update correctly.
// - Dynamic availability updates only apply to Options 2+3; Option 1 is initalized on load.
// - Option variants that don't exist in the product data are automatically disabled.
//   (e.g. if Size:Medium does not come in Color:Red)
// ----------------------------------------------------------------------------

// -------------------------------------
// Dependencies
// -------------------------------------

// Helpers
import iamotaEventHandler from 'scripts/helpers/iamota-event-handler';
import iamFormElement from 'scripts/classes/iamFormElement';

// Styles
import './_product-variant-picker.scss';

// -------------------------------------
// Functionality
// -------------------------------------

class iamProductVariantPicker extends iamFormElement {
  // Class for identifying option containers

  /**
   * Initialize the component when the product form is initialized
   */
  onFormInit() {
    // Subscribe to changes in variant options
    iamotaEventHandler.subscribe('productForm:option:changed', this.onOptionChange.bind(this), {
      context: this.iamForm,
      source: "product-variant-picker.js"
    });

    // Add hover event listeners for swatch options to temporarily display the hovered option
    this.querySelectorAll('.swatch__option input[data-single-option-selector] + label.form__option-label').forEach(swatchOption => {
      swatchOption.addEventListener('mouseenter', this.onOptionHover.bind(this));
      swatchOption.addEventListener('mouseleave', this.updateOptionLabels.bind(this)); // Reverts label on hover exit
    });
    this.initializeColorSwatchOverflow();
  }

  /**
   * Callback when a variant option is changed.
   * Updates the availability, labels, and URL accordingly.
   * @param {Object} event - The event object containing the changed option
   */
  onOptionChange() {
    this.updateOptionAvailability();
    this.updateOptionLabels();
    this.scheduleColorSwatchOverflowUpdate();
  }

  /**
   * Initializes overflow handling for color swatch groups.
   * Adds mobile/tablet "See More Options" toggles when rows overflow.
   */
  initializeColorSwatchOverflow() {
    this.colorSwatchGroups = Array.from(this.querySelectorAll('[data-color-swatch-collapsible="true"]')).map(group => {
      var formElement = group.querySelector('.form__element');
      var button = group.querySelector('[data-swatch-options-toggle]');
      var options = Array.from(group.querySelectorAll('.swatch__option'));
      return {
        group,
        formElement,
        button,
        options
      };
    }).filter(_ref => {
      var formElement = _ref.formElement,
        button = _ref.button,
        options = _ref.options;
      return formElement && button && options.length;
    });
    if (!this.colorSwatchGroups.length) return;
    if (!this._onColorSwatchToggle) {
      this._onColorSwatchToggle = this.onColorSwatchToggle.bind(this);
    }
    this.colorSwatchGroups.forEach(_ref2 => {
      var button = _ref2.button;
      button.addEventListener('click', this._onColorSwatchToggle);
    });
    if (!this._onResizeForColorSwatches) {
      this._onResizeForColorSwatches = this.onColorSwatchesResize.bind(this);
      window.addEventListener('resize', this._onResizeForColorSwatches);
    }
    this.scheduleColorSwatchOverflowUpdate();
  }

  /**
   * Toggles collapsed/expanded state for a color swatch group.
   * @param {Event} event - Click event from the CTA button.
   */
  onColorSwatchToggle(event) {
    var _this$colorSwatchGrou;
    var button = event.currentTarget;
    var swatchGroup = (_this$colorSwatchGrou = this.colorSwatchGroups) === null || _this$colorSwatchGrou === void 0 ? void 0 : _this$colorSwatchGrou.find(_ref3 => {
      var groupButton = _ref3.button;
      return groupButton === button;
    });
    var formElement = swatchGroup === null || swatchGroup === void 0 ? void 0 : swatchGroup.formElement;
    if (!formElement) return;
    var isExpanded = button.getAttribute('aria-expanded') === 'true';
    var nextExpandedState = !isExpanded;
    button.setAttribute('aria-expanded', String(nextExpandedState));
    button.dataset.expanded = String(nextExpandedState);
    button.textContent = nextExpandedState ? button.dataset.lessLabel : button.dataset.moreLabel;
    formElement.classList.toggle('is-collapsed', !nextExpandedState);
  }

  /**
   * Debounced resize handler for color swatch overflow calculations.
   */
  onColorSwatchesResize() {
    window.clearTimeout(this._resizeDebounceTimeout);
    this._resizeDebounceTimeout = window.setTimeout(() => {
      this.scheduleColorSwatchOverflowUpdate();
    }, 120);
  }

  /**
   * Schedules one overflow recalculation per frame.
   */
  scheduleColorSwatchOverflowUpdate() {
    var _this$colorSwatchGrou2;
    if (!((_this$colorSwatchGrou2 = this.colorSwatchGroups) !== null && _this$colorSwatchGrou2 !== void 0 && _this$colorSwatchGrou2.length)) return;
    if (this._overflowRafId) window.cancelAnimationFrame(this._overflowRafId);
    this._overflowRafId = window.requestAnimationFrame(() => {
      this._overflowRafId = null;
      this.updateColorSwatchOverflow();
    });
  }

  /**
   * Calculates swatch row overflow and updates CTA visibility/state.
   */
  updateColorSwatchOverflow() {
    var _this$colorSwatchGrou3;
    if (!((_this$colorSwatchGrou3 = this.colorSwatchGroups) !== null && _this$colorSwatchGrou3 !== void 0 && _this$colorSwatchGrou3.length)) return;
    this.colorSwatchGroups.forEach(_ref4 => {
      var formElement = _ref4.formElement,
        button = _ref4.button,
        options = _ref4.options;
      var rowOffsets = [...new Set(options.map(option => option.offsetTop))].sort((a, b) => a - b);
      var hasOverflowRows = rowOffsets.length > 2;
      if (!hasOverflowRows) {
        formElement.classList.remove('is-collapsed');
        button.hidden = true;
        button.dataset.expanded = 'false';
        button.setAttribute('aria-expanded', 'false');
        button.textContent = button.dataset.moreLabel;
        return;
      }
      var secondRowTop = rowOffsets[1];
      var secondRowItems = options.filter(option => option.offsetTop === secondRowTop);
      var secondRowBottom = Math.max(...secondRowItems.map(option => option.offsetTop + option.offsetHeight));
      formElement.style.setProperty('--swatch-collapsed-height', "".concat(secondRowBottom, "px"));
      button.hidden = false;
      var isExpanded = button.dataset.expanded === 'true';
      button.setAttribute('aria-expanded', String(isExpanded));
      button.textContent = isExpanded ? button.dataset.lessLabel : button.dataset.moreLabel;
      formElement.classList.toggle('is-collapsed', !isExpanded);
    });
  }

  /**
   * Updates the option label based on the user's selection or hover state.
   * @param {Element} option - The DOM element for the option being updated
   * @param {String} label - The new label text to display
   */
  setOptionLabel(option, label) {
    var selectorWrapper = option.closest(iamProductVariantPicker.OPTION_WRAPPER_CLASS);
    var subtitle = selectorWrapper === null || selectorWrapper === void 0 ? void 0 : selectorWrapper.querySelector('.swatch__title .swatch__subtitle');
    if (!subtitle) return;
    subtitle.textContent = String(label || '').toUpperCase(); // Keep parity with initial Liquid casing
  }

  /**
   * Updates the labels for all selected options.
   * Reflects the current selection for each swatch option.
   */
  updateOptionLabels() {
    this.querySelectorAll('.swatch__option input[data-single-option-selector]:checked').forEach(swatchOption => {
      this.setOptionLabel(swatchOption, swatchOption.value); // Set the label to the selected value
    });
  }

  /**
   * Temporarily updates the option label during hover.
   * Displays the option value the user is hovering over.
   * @param {Event} event - The hover event
   */
  onOptionHover(event) {
    var target = event.currentTarget;
    var newLabel = target.getAttribute('title') || target.textContent;
    this.setOptionLabel(target, newLabel); // Temporarily show hovered label
  }

  /**
   * Updates the availability of options based on the current variant selection.
   * Only options 2 and 3 are updated, as option 1 remains available by default.
   */
  updateOptionAvailability() {
    var _this = this;
    // Get the currently selected options
    var selectedOptions = this.iamForm.getCurrentOptions();

    // Get the option wrappers for options 2 and 3
    var optionWrappers = Array.from(this.querySelectorAll(iamProductVariantPicker.OPTION_WRAPPER_CLASS));
    optionWrappers = optionWrappers.slice(1); // Exclude option 1 from availability updates (DOM pre-loaded it)

    // Update the availability for each option
    optionWrappers.forEach((option, index) => {
      var optionIndex = index + 2; // Offset to skip option 1
      var optionInputs = option.querySelectorAll('input[type="radio"], option'); // Get input fields

      // Filter variants to get those available based on selected options
      var availableOptions = this.iamForm.productData.variants
      // Only consider "available" variants (i.e. otherwise Shopify will not allow you to add to cart)
      .filter(variant => variant.available)

      // Filter variants that match selected options
      .filter(variant => {
        return selectedOptions.slice(0, optionIndex - 1) // Extract the relevent selected options
        .every((opt, idx) => variant["option".concat(idx + 1)] === opt.value); // Ensure variant matches selected options
      })

      // Allow external filtering of available variants (i.e. custom logic)
      .filter(/*#__PURE__*/function () {
        var _ref5 = _asyncToGenerator(function* (variant) {
          return yield iamotaEventHandler.applyFilters('productForm:variant:availability', {
            variant
          }, {
            context: _this.iamForm
          });
        });
        return function (_x) {
          return _ref5.apply(this, arguments);
        };
      }())

      // Get the available values for the current option
      .map(variant => variant["option".concat(optionIndex)]);

      // Update availability for the option
      this.setOptionAvailability(optionInputs, availableOptions);
    });
  }

  /**
   * Sets the availability status for the variant options.
   * Toggles the disabled state based on availability.
   * @param {NodeList} listOfOptions - The list of option inputs
   * @param {Array} availableOptions - The options that are currently available
   */
  setOptionAvailability(listOfOptions, availableOptions) {
    listOfOptions.forEach(input => {
      var swatchOption = input.closest('.swatch__option'); // Find the swatch option wrapper
      if (swatchOption) {
        var label = swatchOption.querySelector('.form__option-label');
        var isSwatchDisabled = !availableOptions.includes(input.getAttribute('value')); // Determine if the option is unavailable
        label.classList.toggle('swatch--disabled', isSwatchDisabled); // Toggle the disabled state
      }
    });
  }
}

// Register the custom element with the browser
// Variables
_defineProperty(iamProductVariantPicker, "OPTION_WRAPPER_CLASS", '.selector-wrapper');
customElements.define('iam-variant-picker', iamProductVariantPicker);